import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { Navigate } from 'react-router-dom';
import {
  BuildingOfficeIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { CompanyService } from '../../services/companyService';
import { Company } from '../../types';
import { Role } from '../../types';
import { PageTitle } from '../../components/ui/PageTitle';
import CreateCompanyModal from '../../components/admin/CreateCompanyModal';
import CompanyDetailModal from '../../components/admin/CompanyDetailModal';

const CompanyManagement: React.FC = () => {
  const { user } = useAuth();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateCompanyModalOpen, setIsCreateCompanyModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [successMessage, setSuccessMessage] = useState<string>('');

  const companyService = React.useMemo(() => new CompanyService(), []);

  const loadCompanies = React.useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');
      const companiesData = await companyService.getAllCompanies();
      setCompanies(companiesData);
    } catch {
      setError('陸送会社の取得に失敗しました');
    } finally {
      setIsLoading(false);
    }
  }, [companyService]);

  // useEffect must be called before any conditional returns
  useEffect(() => {
    if (user && user.role === Role.SUPER_ADMIN) {
      loadCompanies();
    }
  }, [loadCompanies, user]);

  // Super Admin権限チェック
  if (!user || user.role !== Role.SUPER_ADMIN) {
    return <Navigate to='/' replace />;
  }

  const handleCreateCompanySuccess = async (company: Company) => {
    setSuccessMessage(`陸送会社「${company.name}」を作成しました。`);
    setIsCreateCompanyModalOpen(false);

    // 会社一覧を再取得
    await loadCompanies();

    // 成功メッセージを5秒後に消す
    setTimeout(() => setSuccessMessage(''), 5000);
  };

  const handleViewDetails = (company: Company) => {
    setSelectedCompany(company);
    setIsDetailModalOpen(true);
  };

  const filteredCompanies = companies.filter(
    company =>
      company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className='min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600'></div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gray-50 dark:bg-gray-900'>
      {/* ヘッダー */}
      <div className='bg-white dark:bg-gray-800 shadow'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='flex justify-between items-center py-6'>
            <div>
              <PageTitle icon={BuildingOfficeIcon} title='陸送会社管理' />
              <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
                登録されている陸送会社の管理
              </p>
            </div>
            <button
              onClick={() => setIsCreateCompanyModalOpen(true)}
              className='inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200'
            >
              <PlusIcon className='h-4 w-4 mr-2' />
              新規会社登録
            </button>
          </div>
        </div>
      </div>

      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        {error && (
          <div className='mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4'>
            <p className='text-sm text-red-800 dark:text-red-200'>{error}</p>
          </div>
        )}

        {successMessage && (
          <div className='mb-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4'>
            <div className='flex items-center'>
              <CheckCircleIcon className='h-5 w-5 text-green-400 mr-2' />
              <p className='text-sm text-green-800 dark:text-green-200'>{successMessage}</p>
            </div>
          </div>
        )}

        {/* 検索バー */}
        <div className='mb-6'>
          <div className='relative'>
            <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
              <MagnifyingGlassIcon className='h-5 w-5 text-gray-400' />
            </div>
            <input
              type='text'
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className='block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500'
              placeholder='会社名またはメールアドレスで検索...'
            />
          </div>
        </div>

        {/* 会社一覧テーブル */}
        <div className='bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden'>
          <div className='px-6 py-4 border-b border-gray-200 dark:border-gray-700'>
            <div className='flex items-center'>
              <BuildingOfficeIcon className='h-6 w-6 text-gray-400 mr-2' />
              <h2 className='text-lg font-medium text-gray-900 dark:text-white'>
                登録会社一覧 ({filteredCompanies.length}社)
              </h2>
            </div>
          </div>

          <div className='overflow-x-auto'>
            <table className='min-w-full divide-y divide-gray-200 dark:divide-gray-700'>
              <thead className='bg-gray-50 dark:bg-gray-700'>
                <tr>
                  <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                    会社名
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                    連絡先
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                    ステータス
                  </th>
                </tr>
              </thead>
              <tbody className='bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700'>
                {filteredCompanies.map(company => (
                  <tr key={company.id} className='hover:bg-gray-50 dark:hover:bg-gray-700'>
                    <td
                      className='px-6 py-4 whitespace-nowrap cursor-pointer'
                      onClick={() => handleViewDetails(company)}
                    >
                      <div className='text-sm font-medium text-gray-900 dark:text-white'>
                        {company.name}
                      </div>
                      <div className='text-sm text-gray-500 dark:text-gray-400'>
                        {company.address || '住所なし'}
                      </div>
                    </td>
                    <td
                      className='px-6 py-4 whitespace-nowrap cursor-pointer'
                      onClick={() => handleViewDetails(company)}
                    >
                      {company.email && (
                        <div className='text-sm text-gray-900 dark:text-white'>{company.email}</div>
                      )}
                      {company.phone && (
                        <div className='text-sm text-gray-500 dark:text-gray-400'>
                          {company.phone}
                        </div>
                      )}
                    </td>
                    <td
                      className='px-6 py-4 whitespace-nowrap cursor-pointer'
                      onClick={() => handleViewDetails(company)}
                    >
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          company.subscriptionStatus === 'active' ||
                          company.subscriptionStatus === 'trial'
                            ? 'bg-green-100 text-green-800 dark:bg-green-800/20 dark:text-green-400'
                            : 'bg-red-100 text-red-800 dark:bg-red-800/20 dark:text-red-400'
                        }`}
                      >
                        {company.subscriptionStatus === 'active' ||
                        company.subscriptionStatus === 'trial'
                          ? 'アクティブ'
                          : '無効'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredCompanies.length === 0 && (
              <div className='text-center py-12'>
                <BuildingOfficeIcon className='mx-auto h-12 w-12 text-gray-400 mb-4' />
                <p className='text-gray-500 dark:text-gray-400'>
                  {searchTerm
                    ? '検索条件に一致する会社が見つかりません'
                    : '登録されている会社がありません'}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* モーダル */}
        <CreateCompanyModal
          isOpen={isCreateCompanyModalOpen}
          onClose={() => setIsCreateCompanyModalOpen(false)}
          onSuccess={handleCreateCompanySuccess}
        />

        <CompanyDetailModal
          isOpen={isDetailModalOpen}
          onClose={() => {
            setIsDetailModalOpen(false);
            setSelectedCompany(null);
          }}
          company={selectedCompany}
          onDeleteSuccess={async () => {
            setSuccessMessage(`陸送会社「${selectedCompany?.name}」を削除しました。`);
            await loadCompanies();
            setTimeout(() => setSuccessMessage(''), 5000);
          }}
          onUpdateSuccess={async () => {
            setSuccessMessage(`陸送会社「${selectedCompany?.name}」を更新しました。`);
            await loadCompanies();
            setTimeout(() => setSuccessMessage(''), 5000);
          }}
        />
      </div>
    </div>
  );
};

export default CompanyManagement;
